/*
 * P1 · 首页
 *
 * 三大区：
 *   ① 上传 / 自然语言入口
 *   ② 我的模板库（3 张彩色封面）
 *   ③ 最近填好的文档
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { message, Button, Modal } from 'antd'
import {
  ArrowUpOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileExcelFilled,
  FileFilled,
  FilePdfFilled,
  FilePptFilled,
  FileTextFilled,
  FileWordFilled,
  LoadingOutlined,
  MessageOutlined,
  SwapOutlined,
  ThunderboltFilled,
  UploadOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { xheyAi, SINGLE_QUERY_COST } from '../api'
import type { CreditsBalance } from '../api'
import type { SkillItem, FileItem } from '../types'
import { useCurrentFile } from '../ctx/FileCtx'
import {
  computeExcelDiff,
  diffToPromptText,
  readFileAsArrayBuffer,
  probeTemplateSampleMatch,
  MatchProbeResult
} from '../utils/excelDiff'
import { ensureSafeFile } from '../utils/sanitizeFileName'
import {
  OFFICE_ACCEPT_TYPES as ACCEPT_TYPES,
  validateOfficeFile
} from '../utils/officeFileTypes'
import { EditSkillModal } from '../components/EditSkillModal'
import {
  trackEnter,
  trackClick,
  trackDialogShow,
  trackDialogClick,
  trackResult
} from '../utils/track'
import { useCredits } from '../ctx/CreditsCtx'
import { usePayment, useVipKind, useAppSelector } from '../../../app/hooks'
import CreditsExhaustedModal from '../components/CreditsExhaustedModal'
import DemoReplayModal from '../demo/DemoReplayModal'
import PointsPackModal from '../../components/PointsPackModal'
import { aiClerkPayContent } from '../utils/paymentContent'
import CONFIG from '../../../constants/config'
import './index.scss'

/** 字节数转人类可读 */
function formatBytes(n?: number): string {
  if (!n || n < 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

// 文件类型 → antd6 实心图标查表（pickFileIconStyle 返回的 type 字符串）
const FILE_FILLED_ICONS: Record<string, React.ComponentType<any>> = {
  'file-excel': FileExcelFilled,
  'file-word': FileWordFilled,
  'file-ppt': FilePptFilled,
  'file-pdf': FilePdfFilled,
  'file-text': FileTextFilled,
  file: FileFilled
}
const FileTypeIcon = ({
  name,
  ...rest
}: {
  name: string
  [k: string]: any
}) => {
  const C = FILE_FILLED_ICONS[name]
  return C ? <C {...rest} /> : null
}

/** 文件名 → antd Icon 类型 + 颜色 token，用于"最近填好的文档"列表的左侧图标 */
function pickFileIconStyle(filename: string): {
  type:
    | 'file-excel'
    | 'file-word'
    | 'file-pdf'
    | 'file-ppt'
    | 'file-text'
    | 'file'
  bg: string
  color: string
} {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { type: 'file-excel', bg: '#E8F5E9', color: '#1B7C3E' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { type: 'file-word', bg: '#E3F2FD', color: '#1565C0' }
  }
  if (ext === 'pdf') {
    return { type: 'file-pdf', bg: '#FFEBEE', color: '#C62828' }
  }
  if (ext === 'pptx' || ext === 'ppt') {
    return { type: 'file-ppt', bg: '#FFF3E0', color: '#D84315' }
  }
  if (ext === 'txt' || ext === 'md') {
    return { type: 'file-text', bg: '#F5F5F5', color: '#616161' }
  }
  return { type: 'file', bg: '#F5F5F5', color: '#757575' }
}

/** 接受 ISO 字符串（"2026-04-27T10:30:00"）或数字毫秒 */
function formatSessionTime(input: string | number | undefined): string {
  if (!input) return ''
  const ts = typeof input === 'number' ? input : new Date(input).getTime()
  if (Number.isNaN(ts)) return ''
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} 天前`
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * 首条消息要消费的"参考样例"载荷：home 上传完写入，work 自动发送时读、读完清掉。
 * 包含 diff 文本（让 agent 看到结构化字段映射）+ 附件元信息（让首条用户气泡渲染
 * 出附件 chip，agent 也拿到 URL 可以二次 read）。
 */
export interface SamplePayload {
  diffText: string
  attachment: {
    url: string
    name: string
    mediaType: string
  }
}
export const SAMPLE_DIFF_PROMPT_KEY = 'aiClerk:samplePayload'
// 「没有现成文档→描述生成」入口:把用户描述写进会话存储,work 页首条消息消费它
export const DESCRIBE_PROMPT_KEY = 'aiClerk:describePrompt'

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const XLS_MEDIA_TYPE = 'application/vnd.ms-excel'
const DOCX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const DOC_MEDIA_TYPE = 'application/msword'

function pickMediaType(filename: string): string {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx') return XLSX_MEDIA_TYPE
  if (ext === 'xls') return XLS_MEDIA_TYPE
  if (ext === 'docx') return DOCX_MEDIA_TYPE
  if (ext === 'doc') return DOC_MEDIA_TYPE
  return 'application/octet-stream'
}

const Home: React.FC = () => {
  const navigate = useNavigate()
  const { setFile } = useCurrentFile()
  const { credits, refresh: refreshCredits } = useCredits()
  const payment = usePayment()
  const vipKind = useVipKind()
  // 切团队后刷新技能库/最近文档:重构版 group slice 是 RTK 普通对象,
  // 直接读 state.group.groupId(非 Immutable .get),放进下方 effect 依赖数组。
  const groupId = useAppSelector((state) => state.group.groupId)
  const [creditsModalVisible, setCreditsModalVisible] = useState(false)
  // 「看示例」演示回放弹窗
  const [demoOpen, setDemoOpen] = useState(false)
  const [pointsPackVisible, setPointsPackVisible] = useState(false)
  // 被余额拦截的发起动作；续费成功后自动重试（'upload'=上传发起，SkillItem=模板发起）
  const pendingStartRef = useRef<null | 'upload' | SkillItem>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sampleInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOverTemplate, setDragOverTemplate] = useState(false)
  const [dragOverSample, setDragOverSample] = useState(false)
  const [naturalInput, setNaturalInput] = useState('')
  /** 用户选好的模板——延迟到点"开始填写"再上传，让用户有机会先添加样例 */
  const [templateFile, setTemplateFile] = useState<File | null>(null)
  /** 用户预先选好的"参考样例"——只存 File 对象，不上传，diff 算完即抛 */
  const [sampleFile, setSampleFile] = useState<File | null>(null)
  /** 模板↔样例的预匹配检测结果（home 页选完两份就跑一次，避免上传后才发现样例不对） */
  const [probeResult, setProbeResult] = useState<MatchProbeResult | null>(null)
  const [probing, setProbing] = useState(false)
  // 我的模板库（/api/user-skills）
  const [skills, setSkills] = useState<SkillItem[]>([])
  const [skillsLoading, setSkillsLoading] = useState(false)
  const [skillsError, setSkillsError] = useState<string>('')

  // 最近填好的文档（/api/files）
  const [recentFiles, setRecentFiles] = useState<FileItem[]>([])
  const [recentFilesLoading, setRecentFilesLoading] = useState(false)
  const [recentFilesError, setRecentFilesError] = useState<string>('')

  // 编辑模板 Modal
  const [editingSkill, setEditingSkill] = useState<SkillItem | null>(null)
  // 「继续使用」时记下要带到 work 页的技能 — 接下来这次上传完成后用到
  const [pendingSkill, setPendingSkill] = useState<SkillItem | null>(null)

  // 拉模板库
  const loadSkills = useCallback(async () => {
    setSkillsLoading(true)
    setSkillsError('')
    try {
      const list = await xheyAi.listUserSkills()
      // 按 updated_at 倒序
      const sorted = [...list].sort(
        (a, b) => (b.updated_at || 0) - (a.updated_at || 0)
      )
      setSkills(sorted)
    } catch (e) {
      setSkillsError(e instanceof Error ? e.message : String(e))
    } finally {
      setSkillsLoading(false)
    }
  }, [])

  // 拉最近文档
  const loadRecentFiles = useCallback(async () => {
    setRecentFilesLoading(true)
    setRecentFilesError('')
    try {
      const list = await xheyAi.listRecentFiles({ limit: 10 })
      const sorted = [...list].sort(
        (a, b) => (b.mtime_ms || 0) - (a.mtime_ms || 0)
      )
      setRecentFiles(sorted)
    } catch (e) {
      setRecentFilesError(e instanceof Error ? e.message : String(e))
    } finally {
      setRecentFilesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSkills()
    loadRecentFiles()
  }, [groupId, loadSkills, loadRecentFiles])

  // 页面进入埋点(只 mount 时上报一次,不随 deps 变化)
  // 需求 #1103 - track_sign 由数据团队按事件分配
  useEffect(() => {
    trackEnter('formFiller', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.pageview.ai_enter_web_ai_form_filler'
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 预匹配探测：模板 + 样例都是 xlsx 时，跑一次轻量字段对齐检测，让用户在跳转前
  // 就知道两份文件像不像。debounce 300ms 防止用户快速切换文件触发多次。
  // Word 不跑（没法用 ExcelJS 解析）；任一未选不跑（什么都没法测）。
  useEffect(() => {
    if (!templateFile || !sampleFile) {
      setProbeResult(null)
      setProbing(false)
      return
    }
    const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
    const sExt = (sampleFile.name.split('.').pop() || '').toLowerCase()
    const isExcelPair =
      ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
    if (!isExcelPair) {
      setProbeResult(null)
      setProbing(false)
      return
    }

    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled) return
      setProbing(true)
      try {
        const [tBuf, sBuf] = await Promise.all([
          readFileAsArrayBuffer(templateFile),
          readFileAsArrayBuffer(sampleFile)
        ])
        if (cancelled) return
        const result = await probeTemplateSampleMatch(tBuf, sBuf)
        if (cancelled) return
        setProbeResult(result)
      } catch (e) {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.warn('[aiClerk] 预匹配探测失败:', e)
        setProbeResult({
          matchPercent: 0,
          matchedHeaders: 0,
          totalHeaders: 0,
          reversed: false,
          templateFilledCount: 0,
          sampleFilledCount: 0,
          parseError: e instanceof Error ? e.message : String(e)
        })
      } finally {
        if (!cancelled) setProbing(false)
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [templateFile, sampleFile])

  // 一键互换：用户点 reversed 提示里的"互换"按钮时把模板和样例对调
  const handleSwapTemplateAndSample = useCallback(() => {
    setTemplateFile((prev) => {
      const wasSample = sampleFile
      setSampleFile(prev)
      return wasSample
    })
  }, [sampleFile])

  // 「上传文档」弹窗:模板信息 + 可选样例上传 + 开始识别按钮
  const [startModalOpen, setStartModalOpen] = useState(false)

  // 上传文档半屏进入埋点(Modal 从 false → true 时上报一次)
  const startModalOpenRef = useRef(false)
  useEffect(() => {
    if (startModalOpen && !startModalOpenRef.current) {
      trackEnter('uploadDoc', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.pageview.ai_enter_web_ai_upload_doc',
        enterFrom: 'web_ai_form_filler'
      })
    }
    startModalOpenRef.current = startModalOpen
  }, [startModalOpen])

  // 触发隐藏 file input 实际选文档。
  // 首页 slot 点击 + Modal 内的"重新上传文档"按钮都走这里;
  // file input 的 onChange (handleTemplateChange) 在选完文件后会
  // setStartModalOpen(true) 把 Modal 拉起来。
  const handlePickTemplate = useCallback(() => {
    // 区分场景:Modal 未开 → 主页上传入口;Modal 已开 → 半屏内"重新上传"
    if (startModalOpen) {
      trackClick('uploadDoc', 'upload_area', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
    } else {
      trackClick('formFiller', 'upload_area', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
      })
    }
    if (fileInputRef.current) fileInputRef.current.click()
  }, [startModalOpen])

  // Modal 内已上传文件卡片的 × ：仅清空模板，保持 Modal 打开
  // （区别于 handleStartModalClose 的"放弃这次上传"语义）
  const handleClearTemplateInModal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (uploading) return
      trackClick('uploadDoc', 'remove_file', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      setTemplateFile(null)
    },
    [uploading]
  )

  const handleTemplateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      const err = validateOfficeFile(file)
      if (err) {
        message.error(err)
        return
      }
      // 文件名 sanitize 在 handleSubmit 上传那一刻 ensureSafeFile 静默处理
      // (空格 / `*` / `?` 等真不安全字符), 不再打扰用户. LLM 加空格场景由
      // 后端 fuzzy resolve helper (#105) 兜住, 不需要前端预警.
      setTemplateFile(file)
      // 选好模板就弹出"添加样例 / 开始识别"弹窗
      setStartModalOpen(true)
    },
    []
  )

  const handleClearTemplate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setTemplateFile(null)
    setSampleFile(null)
    setStartModalOpen(false)
  }, [])

  // 关闭弹窗 = 清空模板 + 样例(用户主动放弃这次上传)
  const handleStartModalClose = useCallback(() => {
    if (uploading) return // 上传中不允许关
    trackClick('uploadDoc', 'close', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    setStartModalOpen(false)
    setTemplateFile(null)
    setSampleFile(null)
  }, [uploading])

  // 选样例（不上传，本地解析）
  const handlePickSample = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    trackClick('uploadDoc', 'upload_sample', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    if (sampleInputRef.current) sampleInputRef.current.click()
  }, [])

  const handleSampleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      const err = validateOfficeFile(file)
      if (err) {
        message.error(`参考样例：${err}`)
        return
      }
      // sanitize 走 handleSubmit 的 ensureSafeFile, 不预警 (LLM 改名走后端 fuzzy)
      setSampleFile(file)
    },
    []
  )

  const handleClearSample = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setSampleFile(null)
  }, [])

  /**
   * 用户点击发起会话前：确保 credits 已就绪。
   *
   * 进入页面时 CreditsCtx 已自动拉 + 故障重试一次；用户实际点击时(常常是几秒后)
   * 如果余额还是 null，多半是首次拉余额遇到瞬态故障且重试也撞上了，这里再补一次
   * 主动 refresh —— 给故障恢复后第二次机会，避免用户必须自己刷新页面。
   *
   * 返回：
   *   - { ok: true, credits }: 有效余额，调用方继续走 available <= 0 等后续校验
   *   - { ok: false }: 已 message.error 提示「网络错误，请刷新重试」，调用方 return
   */
  const ensureCreditsReady = useCallback(async (): Promise<{
    ok: boolean
    credits: CreditsBalance | null
  }> => {
    let c: CreditsBalance | null = credits
    if (!c || c.available == null) {
      c = await refreshCredits()
    }
    if (!c || c.available == null) {
      message.error('网络错误，请刷新重试')
      return { ok: false, credits: null }
    }
    return { ok: true, credits: c }
  }, [credits, refreshCredits])

  // 真正的上传 + diff + 跳转——点"开始填写"才触发
  const handleStart = useCallback(
    async (skipGuard = false) => {
      if (uploading) return
      // skipGuard=true（续费后续会话）跳过余额拦截：信任已付款，余额由后端兜底
      if (!skipGuard) {
        // credits 还没就绪 → 主动 refresh 一次；仍失败已弹「网络错误，请刷新重试」直接 return
        const ready = await ensureCreditsReady()
        if (!ready.ok) return
        // 余额耗尽（<= 0）→ 记下待发起动作 + 弹「积分不足」
        if ((ready.credits?.available ?? 0) <= 0) {
          pendingStartRef.current = 'upload'
          setCreditsModalVisible(true)
          return
        }
      }
      if (!templateFile) {
        message.error('请先选择要填写的模板')
        return
      }
      // 点击"开始识别" click 埋点(在做参数校验前,捕捉用户意图)
      trackClick('uploadDoc', 'start_recognition', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      // 参考样例的可用性校验：必须与模板同类（Excel-Excel 或 Word-Word）
      // —— Excel 走"本地 diff + 附件"；Word 只走"附件"，让 agent 自己 read 样例分析
      const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
      const sExt = sampleFile
        ? (sampleFile.name.split('.').pop() || '').toLowerCase()
        : ''
      const isExcelPair =
        ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
      const isWordPair =
        ['docx', 'doc'].includes(tExt) && ['docx', 'doc'].includes(sExt)
      const sampleUsable = !!sampleFile && (isExcelPair || isWordPair)
      if (sampleFile && !sampleUsable) {
        message.warning(
          '参考样例必须与模板同类型（Excel↔Excel / Word↔Word），本次忽略样例继续上传'
        )
      }
      setUploading(true)
      try {
        // 上传前清洗文件名：空格 / 中文省略号 / 特殊字符会让后端工具 open(path) 报错
        // （历史 case：「模板 - 序号 56_能上传_1_份…xlsx」上传后 excel_edit 读不到）
        const safeTemplate = ensureSafeFile(templateFile)
        const safeSample =
          sampleUsable && sampleFile ? ensureSafeFile(sampleFile) : null
        // 模板走 platform/upload（主文档，OOXML 转换 + 进"最近文档"），
        // 样例走 /upload（composer 附件端点，不进"最近文档"列表，仅做参考资料）。
        // 仅 Excel pair 才需要本地双 buffer 算 diff；Word pair 只上传附件，不读 buffer。
        // 分别 timing 模板/样例 上传耗时,用于 result 埋点的 costTime
        const t0 = Date.now()
        let templateCostTime = 0
        let sampleCostTime = 0
        const [uploadResult, sampleUploadResult, templateBuf, sampleBuf] =
          await Promise.all([
            xheyAi.upload(safeTemplate).then((r) => {
              templateCostTime = Date.now() - t0
              return r
            }),
            safeSample
              ? xheyAi.uploadAttachment(safeSample).then((r) => {
                  sampleCostTime = Date.now() - t0
                  return r
                })
              : Promise.resolve(null),
            isExcelPair
              ? readFileAsArrayBuffer(templateFile)
              : Promise.resolve(null),
            isExcelPair && sampleFile
              ? readFileAsArrayBuffer(sampleFile)
              : Promise.resolve(null)
          ])
        // 主文档上传成功 result 埋点(string1=格式, number1=KB, bool1=是否含样例)
        // xlsx spec: upload_doc.string1 = docx/xlsx/other
        const tFmtRaw = tExt
        const tFmt =
          tFmtRaw === 'docx' || tFmtRaw === 'xlsx' ? tFmtRaw : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'success',
          string1: tFmt,
          number1: Math.round(safeTemplate.size / 1024),
          bool1: !!sampleUploadResult,
          costTime: templateCostTime
        })
        // 样例上传成功 result(若有)
        if (safeSample && sampleUploadResult) {
          const sFmtRaw = sExt
          const sFmt =
            sFmtRaw === 'docx' || sFmtRaw === 'xlsx' ? sFmtRaw : 'other'
          trackResult('uploadDoc', 'upload_sample', {
            requirement_id: 1103,
            track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
            actionResult: 'success',
            string1: sFmt,
            number1: Math.round(safeSample.size / 1024),
            costTime: sampleCostTime
          })
        }
        const { fileUrl, fileName, filePath, refreshUrl } = uploadResult
        // 拼 sample 附件元信息 + （Excel 时）算 diff，一起写 sessionStorage 让 work 消费
        if (sampleUsable && safeSample && sampleUploadResult) {
          let diffText = ''
          if (isExcelPair && templateBuf && sampleBuf) {
            try {
              const diff = await computeExcelDiff(
                templateBuf,
                sampleBuf,
                safeTemplate.name,
                safeSample.name
              )
              diffText = diffToPromptText(diff)
            } catch (e) {
              // 暴露真实错误（早期版本只显示 generic 提示，黑盒）
              // eslint-disable-next-line no-console
              console.warn('[aiClerk] 样例 diff 计算失败:', e)
              const detail = e instanceof Error ? e.message : String(e)
              message.warning({
                content: `参考样例解析失败：${detail}（继续按附件方式发给 agent）`,
                duration: 6
              })
              // 失败时 diffText 仍为空 → 自动降级到 Word 路径（让 agent 自己 read）
            }
          }
          const payload: SamplePayload = {
            diffText, // Word pair 或 Excel diff 失败时 = ''
            attachment: {
              url: sampleUploadResult.fileUrl,
              name: safeSample.name,
              mediaType: pickMediaType(safeSample.name)
            }
          }
          window.sessionStorage.setItem(
            SAMPLE_DIFF_PROMPT_KEY,
            JSON.stringify(payload)
          )
        } else {
          window.sessionStorage.removeItem(SAMPLE_DIFF_PROMPT_KEY)
        }
        setFile({ fileUrl, fileName, filePath, refreshUrl })
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        // 带 pendingSkill (从「继续使用」入口进来) → work 首条 prompt 会 prepend
        // "请使用「<title>」技能完成本次工作"
        const qs = pendingSkill
          ? `?skill=${encodeURIComponent(
              pendingSkill.name
            )}&skillTitle=${encodeURIComponent(
              pendingSkill.display_name || pendingSkill.name
            )}`
          : ''
        setPendingSkill(null)
        navigate(`/ai/clerk/work${qs}`)
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e)
        message.error(`上传失败：${errMsg}`)
        // 失败 result(主文档失败兜底,无法区分到底是模板还是样例失败,默认归到 upload_doc)
        const tFmtRaw = (templateFile.name.split('.').pop() || '').toLowerCase()
        const tFmt =
          tFmtRaw === 'docx' || tFmtRaw === 'xlsx' ? tFmtRaw : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'fail',
          string1: tFmt,
          number1: Math.round(templateFile.size / 1024),
          bool1: !!sampleFile,
          errorMsg: errMsg
        })
        setUploading(false)
      }
    },
    [
      navigate,
      uploading,
      setFile,
      templateFile,
      sampleFile,
      pendingSkill,
      // 余额校验改走 ensureCreditsReady(已内部依赖 credits)，不再直接读 credits
      ensureCreditsReady
    ]
  )

  // 模板「继续使用」发起会话（拦截后重试也走这里）
  const startTemplate = useCallback(
    async (sk: SkillItem) => {
      trackClick('formFiller', 'continue_use_template', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        string1: sk.name
      })
      const t0 = Date.now()
      try {
        const result = await xheyAi.useSkill(sk.name)
        trackResult('formFiller', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
          actionResult: 'success',
          string1: 'template',
          string2: sk.name,
          costTime: Date.now() - t0
        })
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        if (result.hasTemplate) {
          setFile({
            fileUrl: result.fileUrl,
            fileName: result.fileName,
            filePath: result.filePath,
            refreshUrl: result.refreshUrl
          })
        }
        const qs =
          `?skill=${encodeURIComponent(sk.name)}` +
          `&skillTitle=${encodeURIComponent(sk.display_name || sk.name)}`
        navigate(`/ai/clerk/work${qs}`)
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e)
        message.error(`继续使用失败: ${errMsg}`)
        trackResult('formFiller', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
          actionResult: 'fail',
          string1: 'template',
          string2: sk.name,
          costTime: Date.now() - t0,
          errorMsg: errMsg
        })
      }
    },
    [navigate, setFile]
  )

  // 续费成功（增购 / 升级）后「放行容错」：直接续上被拦截的会话，不卡刚刷新的余额——
  // 计费 / 额度有秒级延迟，refresh 可能仍是旧值；余额真不够由后端 SSE 兜底报错
  const runPendingStart = useCallback(() => {
    const pending = pendingStartRef.current
    if (!pending) return
    pendingStartRef.current = null
    if (pending === 'upload') {
      handleStart(true)
    } else {
      startTemplate(pending)
    }
  }, [handleStart, startTemplate])

  // 会员升级成功（vipKind 变化）+ 有待发起动作 → 刷新余额 + 放行容错续会话
  // 用 vipKind 变化而非弹窗关闭判断，避免用户「取消升级」也被误续
  const prevVipKindRef = useRef(vipKind)
  useEffect(() => {
    // 升级成功（vipKind 变化）总刷新余额；有待发起动作时再放行容错续会话
    if (prevVipKindRef.current !== vipKind) {
      refreshCredits()
      if (pendingStartRef.current) {
        runPendingStart()
      }
    }
    prevVipKindRef.current = vipKind
  }, [vipKind, refreshCredits, runPendingStart])

  // 拖拽到模板槽
  const handleTemplateDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOverTemplate(true)
  }, [])
  const handleTemplateDragLeave = useCallback(() => {
    setDragOverTemplate(false)
  }, [])
  const handleTemplateDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverTemplate(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const err = validateOfficeFile(file)
    if (err) {
      message.error(err)
      return
    }
    setTemplateFile(file)
    // 拖拽到首页 slot 也打开 Modal，跟点击+选文件路径一致
    setStartModalOpen(true)
  }, [])

  // 拖拽到样例槽
  const handleSampleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(true)
  }, [])
  const handleSampleDragLeave = useCallback(() => {
    setDragOverSample(false)
  }, [])
  const handleSampleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const err = validateOfficeFile(file)
    if (err) {
      message.error(`参考样例：${err}`)
      return
    }
    setSampleFile(file)
  }, [])

  // 「没有现成文档」入口:用户只描述需求,没有要上传的模板。仍调用上传接口(传空内容),
  // 后端据此返回一份空白文档链接;再把用户描述作为首条消息带进 work 页,让 AI 在这份
  // 空白文档上按需求建表并填好。整体复用上传那套链路(余额拦截 + setFile + 跳转)。
  const handleNaturalSubmit = useCallback(async () => {
    if (uploading) return
    const text = naturalInput.trim()
    if (!text) return
    // 描述生成同样消耗积分,走和上传一致的余额拦截
    const ready = await ensureCreditsReady()
    if (!ready.ok) return
    if ((ready.credits?.available ?? 0) <= 0) {
      setCreditsModalVisible(true)
      return
    }
    // 用户意图:点击「生成」或按 Enter 发文字描述 → 即将调用接口
    trackClick('formFiller', 'start_recognition', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
      string1: 'natural_text'
    })
    setUploading(true)
    const t0 = Date.now()
    try {
      // 上传的内容不需要传:不传 file,后端按"空白文档"返回一份新建文档的链接
      const { fileUrl, fileName, filePath, refreshUrl } = await xheyAi.upload()
      trackResult('formFiller', 'upload_doc', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
        actionResult: 'success',
        string1: 'natural_text',
        costTime: Date.now() - t0
      })
      setFile({ fileUrl, fileName, filePath, refreshUrl })
      // 用户描述交给 work 页首条消息消费(见 work/index.tsx 自动首条消息逻辑)
      try {
        window.sessionStorage.setItem(DESCRIBE_PROMPT_KEY, text)
      } catch {
        /* 忽略 */
      }
      // 描述生成 = 新会话
      try {
        window.localStorage.removeItem('aiClerk:conversationId')
      } catch {
        /* 忽略 */
      }
      navigate('/ai/clerk/work')
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e)
      message.error(`生成失败：${errMsg}`)
      trackResult('formFiller', 'upload_doc', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
        actionResult: 'fail',
        string1: 'natural_text',
        costTime: Date.now() - t0,
        errorMsg: errMsg
      })
      setUploading(false)
    }
  }, [uploading, naturalInput, ensureCreditsReady, setFile, navigate])

  return (
    <div className="aiClerk-home">
      {/* Hero:去掉左侧装饰 icon,只留标题 + 副标题(更克制) */}
      <div className="aiClerk-home__hero">
        <div className="aiClerk-home__hero-meta">
          <h1 className="aiClerk-home__hero-title">AI 填表员</h1>
          <p className="aiClerk-home__hero-sub">
            把 Word / Excel 交给我，我把团队里正确的照片一张一张填进去
          </p>
          {/* 「看示例演示」入口暂时隐藏(demo 回放功能待定,就绪后取消注释恢复)。
              DemoReplayModal 与 demoOpen 状态保留,恢复按钮即可重新打开。 */}
          {/* <button
            type="button"
            className="aiClerk-home__demo-entry"
            onClick={() => {
              // 「看示例演示」点击埋点(神策自动带点击时间)
              trackClick('formFiller', 'view_demo', {
                requirement_id: 1103,
                track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
              })
              setDemoOpen(true)
            }}
          >
            看示例演示
            <span className="aiClerk-home__demo-entry-play">▶</span>
          </button> */}
        </div>
      </div>

      <DemoReplayModal visible={demoOpen} onClose={() => setDemoOpen(false)} />

      {/* 上传文档 */}
      <h2 className="aiClerk-home__section-title">上传文档</h2>
      <div className="aiClerk-home__start">
        <div className="aiClerk-home__slots">
          {/* ── 左:上传文档(主入口) ──
              点击 = 先拉起 file picker 选文档,选完才弹 Modal(由
              handleTemplateChange 内的 setStartModalOpen 触发)。
              这样用户操作链路最短:点上传 → 选文件 → Modal 出现可加样例/直接开始,
              比"点击 → Modal → 再点上传 → 选文件 → Modal"少一步。
              拖拽 drop 同样直接经过 handleTemplateDrop 弹 Modal,不需要额外动作。 */}
          <div
            className="aiClerk-home__slot aiClerk-home__slot--template"
            onClick={handlePickTemplate}
            onDragOver={handleTemplateDragOver}
            onDragLeave={handleTemplateDragLeave}
            onDrop={handleTemplateDrop}
          >
            <div className="aiClerk-home__slot-template-icon-wrap">
              <UploadOutlined className="aiClerk-home__slot-template-icon" />
            </div>
            <div className="aiClerk-home__slot-template-main">
              <div className="aiClerk-home__slot-template-title">
                上传一份要填的文档
              </div>
              <div className="aiClerk-home__slot-template-desc">
                支持 .docx / .xlsx 直接拖进来或点击上传
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_TYPES}
              hidden
              onChange={handleTemplateChange}
            />
          </div>

          {/* ── 右:描述需求生成(辅入口) ──
              没有现成文档的用户在这里直接描述需求,AI 生成空白表再填。
              复用 naturalInput / handleNaturalSubmit(提交时带 prompt 跳到 work 页)。
              视觉上比左侧上传卡克制,保持"上传为主、描述为辅"。 */}
          <div className="aiClerk-home__describe">
            <div className="aiClerk-home__describe-head">
              <div className="aiClerk-home__describe-title">没有现成文档？</div>
              <div className="aiClerk-home__describe-sub">
                说一句，AI 帮你生成空白表再填
              </div>
            </div>
            <div className="aiClerk-home__describe-box">
              <textarea
                className="aiClerk-home__describe-input"
                placeholder={`例：做一张 ${
                  new Date().getMonth() + 1
                } 月施工验收台账，要照片 / 拍摄人 / 时间 / 地点…`}
                value={naturalInput}
                onChange={(e) => setNaturalInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleNaturalSubmit()
                  }
                }}
              />
              <div className="aiClerk-home__describe-actions">
                <button
                  type="button"
                  className="aiClerk-home__describe-send"
                  onClick={handleNaturalSubmit}
                >
                  生成
                </button>
              </div>
            </div>
            <div className="aiClerk-home__describe-hint">
              <MessageOutlined className="aiClerk-home__describe-hint-icon" />
              <span>说半句也行，AI 会一步步问你补全</span>
            </div>
          </div>
        </div>
      </div>

      {/* 模板库:无模板时整个区块(标题+列表)不渲染 */}
      {skills.length > 0 && (
        <>
          <h2 className="aiClerk-home__section-title">我的模板库</h2>
          <div className="aiClerk-home__templates">
            {skills.map((sk) => (
              <div key={sk.name} className="aiClerk-home__template">
                <div className="aiClerk-home__template-name">
                  {sk.display_name}
                </div>
                {/* summary 是给用户看的业务描述; description 是 LLM 触发词,
                  不该展示. 老 skills 没 summary 时 fallback description (过渡期) */}
                {(sk.summary || sk.description) && (
                  <div className="aiClerk-home__template-desc">
                    {sk.summary || sk.description}
                  </div>
                )}
                {sk.updated_at && (
                  <div className="aiClerk-home__template-meta">
                    {formatSessionTime(sk.updated_at)}
                  </div>
                )}
                <div className="aiClerk-home__template-actions">
                  <button
                    className="aiClerk-home__btn aiClerk-home__btn--primary"
                    onClick={async () => {
                      // credits 未就绪 → 主动 refresh；仍失败已弹「网络错误，请刷新重试」
                      const ready = await ensureCreditsReady()
                      if (!ready.ok) return
                      // 余额耗尽（<= 0）→ 记下待发起动作 + 弹「积分不足」
                      if ((ready.credits?.available ?? 0) <= 0) {
                        pendingStartRef.current = sk
                        setCreditsModalVisible(true)
                        return
                      }
                      startTemplate(sk)
                    }}
                  >
                    继续使用
                  </button>
                  <button
                    className="aiClerk-home__btn aiClerk-home__btn--ghost"
                    onClick={() => {
                      trackClick('formFiller', 'edit_template', {
                        requirement_id: 1103,
                        track_sign:
                          'aai.b8134.click.ai_click_web_ai_form_filler',
                        string1: sk.name
                      })
                      setEditingSkill(sk)
                    }}
                  >
                    编辑
                  </button>
                  <button
                    className="aiClerk-home__btn aiClerk-home__btn--danger"
                    onClick={() => {
                      trackClick('formFiller', 'delete_template', {
                        requirement_id: 1103,
                        track_sign:
                          'aai.b8134.click.ai_click_web_ai_form_filler',
                        string1: sk.name
                      })
                      // 弹窗曝光埋点(antd Modal.confirm 没有 afterShow 钩子,
                      // 视同 click 即曝光,前端默认 Modal 同步显示,不存在曝光失败的情况)
                      // spec 仅要求 popTitle,不带 string1
                      trackDialogShow('formFiller', 'confirm_delete_template', {
                        requirement_id: 1103,
                        track_sign:
                          'aai.b8134.pageview.ai_dialog_show_web_ai_form_filler'
                      })
                      Modal.confirm({
                        title: `删除模板「${sk.display_name || sk.name}」?`,
                        content:
                          '删除后无法恢复, 模板里的示例文件 / 业务描述都会一起删掉.',
                        okText: '删除',
                        okType: 'danger',
                        cancelText: '取消',
                        onOk: async () => {
                          // spec 仅要求 popTitle / clickItem,不带 string1
                          trackDialogClick(
                            'formFiller',
                            'confirm_delete_template',
                            'confirm',
                            {
                              requirement_id: 1103,
                              track_sign:
                                'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                            }
                          )
                          try {
                            await xheyAi.deleteUserSkill(sk.name)
                            message.success(
                              `已删除「${sk.display_name || sk.name}」`
                            )
                            loadSkills()
                          } catch (e) {
                            message.error(
                              `删除失败: ${
                                e instanceof Error ? e.message : String(e)
                              }`
                            )
                          }
                        },
                        onCancel: () => {
                          trackDialogClick(
                            'formFiller',
                            'confirm_delete_template',
                            'cancel',
                            {
                              requirement_id: 1103,
                              track_sign:
                                'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                            }
                          )
                        }
                      })
                    }}
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* 最近填好的文档:无文档时整个区块(标题+列表)不渲染 */}
      {recentFiles.length > 0 && (
        <>
          <h2 className="aiClerk-home__section-title">最近填好的文档</h2>
          <div className="aiClerk-home__recent">
            {recentFiles.map((f) => {
              const iconStyle = pickFileIconStyle(f.filename)
              // 内容部分(图标 + 文件信息 + 右侧动作标签)抽出来,
              // 有下载链接 → 整条用 <a> 包裹直接触发下载;无链接 → 用 <div> 显示禁用态
              const inner = (
                <>
                  <div
                    className="aiClerk-home__recent-icon"
                    style={{ background: iconStyle.bg, color: iconStyle.color }}
                  >
                    <FileTypeIcon name={iconStyle.type} />
                  </div>
                  <div className="aiClerk-home__recent-main">
                    <div
                      className="aiClerk-home__recent-name"
                      title={f.filename}
                    >
                      {f.filename}
                    </div>
                    <div className="aiClerk-home__recent-meta">
                      {[
                        formatSessionTime(f.mtime_ms),
                        formatBytes(f.size_bytes)
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <div className="aiClerk-home__recent-actions">
                    {f.download_url ? (
                      <span>下载</span>
                    ) : (
                      <span className="aiClerk-home__recent-disabled">
                        下载链接缺失
                      </span>
                    )}
                  </div>
                </>
              )
              return f.download_url ? (
                <a
                  key={f.path}
                  href={f.download_url}
                  download={f.filename}
                  className="aiClerk-home__recent-item"
                  onClick={() => {
                    trackClick('formFiller', 'download_doc', {
                      requirement_id: 1103,
                      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
                      string1: f.path
                    })
                  }}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={f.path}
                  className="aiClerk-home__recent-item is-disabled"
                >
                  {inner}
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* 上传模板后弹窗:模板信息 + 可选样例 + 开始识别 (按 Figma 一比一还原)
          ⚠ zIndex 显式调低到 790(默认 antd Modal 是 1000):
            分层约定 — 业务弹窗 790 < antd 默认 1000 < 开通弹窗 1001 < 客服/提示 1002+
            非会员/积分不足触发的开通弹窗(VipPaymentModal 1001)必须能盖住这个上传 Modal,
            否则会出现「开通弹窗已弹但被上传弹窗压在下面看不见」(见 issue 截图)。 */}
      <Modal
        title={null}
        open={startModalOpen}
        onCancel={handleStartModalClose}
        mask={{ closable: !uploading }}
        closable={!uploading}
        footer={null}
        width={720}
        destroyOnHidden
        zIndex={790}
        wrapClassName="aiClerk-home__start-modal-wrap"
      >
        {/* 标题 */}
        <div className="aiClerk-home__start-modal-head">
          <h2 className="aiClerk-home__start-modal-h2">上传文档</h2>
        </div>

        {/* ─── 模板文档:已上传 → 文件卡;未上传 → 紫色虚边上传卡 */}
        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">上传文档</div>
          {templateFile ? (
            <div className="aiClerk-home__start-modal-file-card">
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                <FileTypeIcon
                  name={
                    /\.(docx|doc)$/i.test(templateFile.name)
                      ? 'file-word'
                      : /\.(pptx|ppt)$/i.test(templateFile.name)
                        ? 'file-ppt'
                        : 'file-excel'
                  }
                  className="aiClerk-home__start-modal-file-icon"
                />
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={templateFile.name}
                >
                  {templateFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  <span>{(templateFile.size / 1024).toFixed(0)} KB</span>
                </div>
              </div>
              <button
                type="button"
                className="aiClerk-home__start-modal-file-close"
                onClick={handleClearTemplateInModal}
                aria-label="移除模板"
                disabled={uploading}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty">
              <span className="aiClerk-home__start-modal-sample-icon">
                <ArrowUpOutlined />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传一份要填的文档
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  支持{' '}
                  <span className="aiClerk-home__start-modal-ext">.docx</span>{' '}
                  <span className="aiClerk-home__start-modal-ext">.xlsx</span>{' '}
                  格式
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickTemplate}
                disabled={uploading}
              >
                上传文档
              </Button>
            </div>
          )}
        </div>

        {/* ─── 样例(可选)区 */}
        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">
            也可上传
            <strong className="aiClerk-home__start-modal-block-label-em">
              【样例】
            </strong>
            提高填写准确率
            <span className="aiClerk-home__start-modal-block-label-tag">
              可选
            </span>
          </div>

          {sampleFile ? (
            /* 已上传样例:文件卡 + 替换按钮 */
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--filled${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                {/\.(docx|doc)$/i.test(sampleFile.name) ? (
                  <FileWordFilled className="aiClerk-home__start-modal-file-icon" />
                ) : (
                  <FileExcelFilled className="aiClerk-home__start-modal-file-icon" />
                )}
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={sampleFile.name}
                >
                  {sampleFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  {(sampleFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          ) : (
            /* 未上传样例:提示卡 + 上传按钮 */
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <span className="aiClerk-home__start-modal-sample-icon">
                <ThunderboltFilled />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传样例,让识别更准
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  再传一份「之前填好的」样例,AI 会对比学习你的填写习惯,
                  提高填写正确率
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          )}
        </div>

        {/* 预匹配检测条(简化:只显示重要的) */}
        {(probing || probeResult) && (
          <div className="aiClerk-home__probe" style={{ marginTop: 12 }}>
            {probing && (
              <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--neutral">
                <LoadingOutlined /> 正在检测样例与模板的字段匹配…
              </div>
            )}
            {!probing && probeResult && !probeResult.parseError && (
              <>
                {probeResult.matchPercent >= 70 && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--ok">
                    <CheckCircleOutlined /> 字段匹配良好(
                    {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                    列同名)
                  </div>
                )}
                {probeResult.matchPercent >= 30 &&
                  probeResult.matchPercent < 70 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--warn">
                      <WarningOutlined /> 仅 {probeResult.matchPercent}%
                      列对得上(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}
                      ),确认是同类型表?
                    </div>
                  )}
                {probeResult.matchPercent < 30 &&
                  probeResult.totalHeaders > 0 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--err">
                      <CloseCircleOutlined /> 看起来不是同类型表(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                      列匹配)
                    </div>
                  )}
                {probeResult.reversed && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--info">
                    <SwapOutlined /> 模板槽里的文件填得更满,是不是放反了?
                    <button
                      type="button"
                      className="aiClerk-home__probe-swap"
                      onClick={handleSwapTemplateAndSample}
                      disabled={uploading}
                    >
                      一键互换
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 隐藏的样例 input */}
        <input
          ref={sampleInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          hidden
          onChange={handleSampleChange}
        />

        {/* footer:只保留主 CTA(样例本就可选,无需"跳过"按钮重复表达) */}
        <div className="aiClerk-home__start-modal-footer">
          <Button
            type="primary"
            size="large"
            className="aiClerk-home__start-btn aiClerk-home__start-modal-cta"
            onClick={() => handleStart()}
            disabled={!templateFile || uploading}
            loading={uploading}
          >
            {uploading ? '上传中…' : '开始识别'}
          </Button>
        </div>
      </Modal>

      {/* 编辑模板 Modal */}
      <EditSkillModal
        skill={editingSkill}
        open={!!editingSkill}
        onClose={() => setEditingSkill(null)}
        onSaved={loadSkills}
      />

      {/* 积分不足拦截弹窗 + 增购积分包 */}
      <CreditsExhaustedModal
        visible={creditsModalVisible}
        onClose={() => setCreditsModalVisible(false)}
        available={credits?.available ?? 0}
        cost={SINGLE_QUERY_COST}
        vipKind={vipKind}
        onBuyPack={() => {
          setCreditsModalVisible(false)
          // 关上传弹窗,避免与 PointsPackModal(z 1100) 层级竞态闪烁
          // (上传弹窗 zIndex 790 < PointsPackModal/开通弹窗,但 React 重渲时 stacking
          //  context 偶发重建会让它瞬间浮上来)。用户取消付款后可重新点「上传文档」继续,
          //  templateFile 状态保留。
          setStartModalOpen(false)
          setPointsPackVisible(true)
        }}
        onUpgrade={() => {
          setCreditsModalVisible(false)
          // 关上传弹窗（理由同上 onBuyPack）；付款成功后 vipKind 变化 → useEffect
          // 触发 runPendingStart 自动续上 handleStart，不需要重新看到上传弹窗。
          setStartModalOpen(false)
          // 积分增量埋点：升级/开通会员订单归 source_detail=aiFormFiller
          payment.openVipByKind(
            vipKind || CONFIG.VIP_KIND.VIP,
            'aiFormFiller',
            false,
            aiClerkPayContent({
              isVip: vipKind > 0,
              vipKind,
              usedPoints: credits?.consumed ?? 0
            })
          )
        }}
      />
      <PointsPackModal
        visible={pointsPackVisible}
        onClose={() => setPointsPackVisible(false)}
        source="aiFormFillerBuyCredits"
        onPaymentSuccess={() => {
          setPointsPackVisible(false)
          refreshCredits()
          runPendingStart()
        }}
      />
    </div>
  )
}

export default Home
